import { useEffect, useRef, useState } from 'react';
import { TrashIcon, PencilSquareIcon } from '@heroicons/react/20/solid';
import request from '../../../../lib/request';

const EditIngredient = ({
  ingredients,
  total,
  save,
  isIngredientDialogOpen,
  openPayDialog,
  user,
  credit,
  getCredit,
}) => {
  const analyzingRef = useRef(null);
  const analyzingTimeRef = useRef(0);
  const apiControllerRef = useRef(new AbortController());
  const editDialogRef = useRef(null);

  const [list, setList] = useState([]);
  const [totalCalories, setTotalCalories] = useState({
    calories: 0,
    proteins: 0,
    carbohydrates: 0,
    fats: 0,
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzingStatus, setAnalyzingStatus] = useState('Analyzing started');
  const [completeNotification, setCompleteNotification] = useState(false);
  const [newIngredient, setNewIngredient] = useState(null);
  const [aiResult, setAiResult] = useState([]);
  const [isEditingIndex, setIsEditingIndex] = useState(null);

  useEffect(() => {
    if (isIngredientDialogOpen) {
      setList(ingredients);
      setTotalCalories(total);
    }
  }, [isIngredientDialogOpen]);

  const updateIngredientList = () => {
    save(list, totalCalories);
  };

  const removeIngredient = (index) => () => {
    if (window.confirm('Are you sure you want to remove this ingredient?')) {
      Object.entries(list[index]).forEach(([key, value]) => {
        setTotalCalories((prev) => ({
          ...prev,
          [key]: prev[key] - Number(value),
        }));
      });
      setList((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const analyzeDescription = async () => {
    if (!newIngredient.name) {
      return alert('Please enter an ingredient name');
    }

    setIsAnalyzing(true);

    analyzingRef.current = setInterval(() => {
      analyzingTimeRef.current++;
      if (analyzingTimeRef.current === 1) {
        setAnalyzingStatus('1. Analyze description...');
      }
      if (analyzingTimeRef.current === 7) {
        setAnalyzingStatus('2. Calculating each portion...');
      }
      if (analyzingTimeRef.current === 12) {
        setAnalyzingStatus('3. Finalizing result...');
      }
      if (analyzingTimeRef.current === 20) {
        setAnalyzingStatus('Still processing...');
      }
      if (analyzingTimeRef.current === 25) {
        setAnalyzingStatus('Finalizing result...');
      }
      if (analyzingTimeRef.current === 30) {
        setAnalyzingStatus('Analyzing failed. Please try again.');
        setTimeout(() => {
          apiControllerRef.current.abort();
        }, 1000);
      }
    }, 1000);

    try {
      const { result } = await request.post(
        '/meal/ai-scan',
        { description: `${newIngredient.name} ${newIngredient.portion}g` },
        apiControllerRef.current.signal
      );
      if (result?.ingredients) {
        const newCalculation = {
          portion: 0,
          calories: 0,
          carbohydrates: 0,
          fats: 0,
          proteins: 0,
        };

        result.ingredients.forEach((item) => {
          newCalculation.portion += item.portion;
          newCalculation.calories += item.calories;
          newCalculation.carbohydrates += item.carbohydrates;
          newCalculation.fats += item.fats;
          newCalculation.proteins += item.proteins;
        });

        setNewIngredient((prev) => ({
          ...prev,
          portion: Number(newCalculation.portion.toFixed(1)),
          calories: Number(newCalculation.calories.toFixed(1)),
          carbohydrates: Number(newCalculation.carbohydrates.toFixed(1)),
          fats: Number(newCalculation.fats.toFixed(1)),
          proteins: Number(newCalculation.proteins.toFixed(1)),
        }));
        setAiResult(result.ingredients);
      }
    } catch (err) {
      if (err.name === 'CanceledError') {
        clearInterval(analyzingRef.current);
        setAnalyzingStatus('Analyzing started');
        setIsAnalyzing(false);
        analyzingTimeRef.current = 0;
        apiControllerRef.current = new AbortController();
        return alert('AI Analyzing failed. Please try again.');
      }
      if (err.response.data.error === 'OverCreditLimit') {
        clearInterval(analyzingRef.current);
        setAnalyzingStatus('Analyzing started');
        setIsAnalyzing(false);
        analyzingTimeRef.current = 0;
        apiControllerRef.current = new AbortController();
        alert(
          'You have reached the maximum number of AI Scans. Please upgrade to PREMIUM to continue.'
        );
        return openPayDialog();
      }
    }

    clearInterval(analyzingRef.current);
    setAnalyzingStatus('Analyzing started');
    setIsAnalyzing(false);
    analyzingTimeRef.current = 0;
    setCompleteNotification(true);
    setTimeout(() => {
      setCompleteNotification(false);
    }, 800);
    getCredit();
  };

  const openAddCustomIngredient = () => {
    const ingredient = {
      calories: 0,
      carbohydrates: 0,
      fats: 0,
      name: '',
      portion: 0,
      proteins: 0,
    };
    setNewIngredient(ingredient);
    openEditDialog();
  };

  const editExistingIngredient = (index) => () => {
    setNewIngredient(list[index]);
    setIsEditingIndex(index);
    openEditDialog();
  };

  const openEditDialog = () => {
    if (editDialogRef.current) {
      editDialogRef.current.showModal();
    }
  };

  const closeEditDialog = () => {
    if (isAnalyzing) {
      apiControllerRef.current.abort('Cancelled');
    }
    if (editDialogRef.current) {
      editDialogRef.current.close();
    }
    setIsEditingIndex(null);
    setNewIngredient(null);
    setAiResult([]);
    setIsAnalyzing(false);
  };

  const addNewIngredientToList = () => {
    if (!newIngredient.name) {
      alert('Please enter an ingredient name');
      return;
    }

    setList((prev) => [
      ...prev,
      {
        calorie: Number(newIngredient.calories),
        carbohydrates: Number(newIngredient.carbohydrates),
        fats: Number(newIngredient.fats),
        name: newIngredient.name,
        portion: Number(newIngredient.portion),
        proteins: Number(newIngredient.proteins),
      },
    ]);
    setTotalCalories((prev) => ({
      ...prev,
      calories: prev.calories + Number(newIngredient.calories),
      proteins: prev.proteins + Number(newIngredient.proteins),
      carbohydrates: prev.carbohydrates + Number(newIngredient.carbohydrates),
      fats: prev.fats + Number(newIngredient.fats),
    }));
    setNewIngredient(null);
    setAiResult([]);
    closeEditDialog();
  };

  const updateExistingIngredient = () => {
    if (typeof isEditingIndex !== 'number') {
      return;
    }

    const newCalculation = { ...totalCalories };
    newCalculation.calories -= list[isEditingIndex].calories;
    newCalculation.proteins -= list[isEditingIndex].proteins;
    newCalculation.carbohydrates -= list[isEditingIndex].carbohydrates;
    newCalculation.fats -= list[isEditingIndex].fats;

    setList((prev) => {
      const newList = [...prev];
      newList[isEditingIndex] = newIngredient;
      return newList;
    });
    setTotalCalories({
      calories: newCalculation.calories + Number(newIngredient.calories),
      proteins: newCalculation.proteins + Number(newIngredient.proteins),
      carbohydrates:
        newCalculation.carbohydrates + Number(newIngredient.carbohydrates),
      fats: newCalculation.fats + Number(newIngredient.fats),
    });
    setNewIngredient(null);
    setAiResult([]);
    setIsEditingIndex(null);
    closeEditDialog();
  };

  return (
    <div className=''>
      <div className='mb-4'>
        <div className='opacity-40'>Your plate</div>
        <div className='text-xl font-bold text-accent'>
          {totalCalories.calories} Cal
        </div>
      </div>
      <div className='divide-y divide-tertiary space-y-4 max-h-96 overflow-y-auto scrollbar-hidden rounded-xl h-full'>
        {list.map((item, i) => (
          <div
            key={i}
            className='pt-4'
          >
            <div className='flex justify-between gap-2 mb-2 px-1'>
              <div className='flex gap-2 items-end'>
                <div className='font-bold line-clamp-1'>{item.name}</div>
                <div className='opacity-40 flex-shrink-0 text-sm'>
                  {item.portion} g
                </div>
              </div>
              <div className='flex gap-2 items-center'>
                <PencilSquareIcon
                  className='size-5 opacity-40'
                  onClick={editExistingIngredient(i)}
                />
                <TrashIcon
                  className='size-5 opacity-40'
                  onClick={removeIngredient(i)}
                />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-1'>
              <div className='flex justify-between items-center border rounded-full px-2 py-1 bg-tertiary/30'>
                <div>Calories</div>
                <div>{item.calories} Cal</div>
              </div>
              <div className='flex justify-between items-center border rounded-full px-2 py-1 border-protein bg-protein/30'>
                <div>Protein</div>
                <div>{item.proteins} g</div>
              </div>
              <div className='flex justify-between items-center border rounded-full px-2 py-1 border-carbs bg-carbs/30'>
                <div>Carbs</div>
                <div>{item.carbohydrates} g</div>
              </div>
              <div className='flex justify-between items-center border rounded-full px-2 py-1 border-fats bg-fats/30'>
                <div>Fat</div>
                <div>{item.fats} g</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className='flex gap-2 items-center flex-shrink-0 pt-4'>
        <button
          className='flex-1 px-3 py-2 gap-2 rounded-full text-center border border-accent text-accent font-bold'
          onClick={openAddCustomIngredient}
        >
          Add more food
        </button>
        <button
          className='flex-1 px-3 py-2 gap-2 rounded-full text-center bg-accent text-black font-bold'
          onClick={updateIngredientList}
        >
          Save ingredients
        </button>
      </div>

      <dialog
        ref={editDialogRef}
        className='w-full h-full max-w-lg max-h-full bg-transparent'
        onCancel={closeEditDialog}
      >
        <div
          className={`bg-secondary fixed bottom-0 w-full max-w-lg rounded-t-3xl px-3 py-6 text-white flex flex-col border border-b-0 border-tertiary`}
        >
          <div className='flex items-center justify-between mb-2'>
            <div className='font-bold'>Add ingredient</div>
            <span
              className='text-2xl cursor-pointer'
              onClick={closeEditDialog}
            >
              🅧
            </span>
          </div>
          {newIngredient && (
            <div className={`mb-4 ${isAnalyzing ? 'animate-pulse' : ''}`}>
              <div className='flex items-center justify-between gap-1 mb-1'>
                <div className='w-1/2'>
                  <input
                    type='text'
                    value={newIngredient.name}
                    placeholder='Ingredient name'
                    onChange={(e) =>
                      setNewIngredient({
                        ...newIngredient,
                        name: e.target.value,
                      })
                    }
                    className='rounded-full p-2 bg-tertiary text-white w-full'
                  />
                </div>
                <div className='flex items-center gap-2 w-1/2 justify-between rounded-full px-2 py-1 bg-tertiary'>
                  <div>Portion</div>
                  <div className='flex items-center gap-2'>
                    <input
                      type='number'
                      value={newIngredient.portion}
                      onChange={(e) =>
                        setNewIngredient({
                          ...newIngredient,
                          portion: e.target.value,
                        })
                      }
                      className='rounded-full px-2 py-1 bg-transparent text-white w-full text-center'
                    />
                    <span>g</span>
                  </div>
                </div>
              </div>
              <div className='mb-4'>
                {aiResult.length > 0 && (
                  <div className='flex items-center gap-1 text-xs flex-wrap italic text-white/50 justify-center'>
                    {aiResult.map((item, i) => (
                      <span key={i}>
                        {item.name}
                        {i < aiResult.length - 1 && ', '}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className='grid grid-cols-2 gap-1'>
                <div className='flex justify-between items-center border rounded-full px-2 py-1 bg-tertiary/30'>
                  <div>Calories</div>
                  <div className='flex items-center gap-2'>
                    <input
                      type='number'
                      value={newIngredient.calories}
                      onChange={(e) =>
                        setNewIngredient({
                          ...newIngredient,
                          calories: e.target.value,
                        })
                      }
                      className='rounded-full px-2 py-1 bg-transparent text-white w-full text-center'
                    />
                    <span>Cal</span>
                  </div>
                </div>
                <div className='flex justify-between items-center border rounded-full px-2 py-1 border-protein bg-protein/30'>
                  <div>Protein</div>
                  <div>
                    <div className='flex items-center gap-2'>
                      <input
                        type='number'
                        value={newIngredient.proteins}
                        onChange={(e) =>
                          setNewIngredient({
                            ...newIngredient,
                            proteins: e.target.value,
                          })
                        }
                        className='rounded-full px-2 py-1 bg-transparent text-white w-full text-center'
                      />
                      <span>g</span>
                    </div>
                  </div>
                </div>
                <div className='flex justify-between items-center border rounded-full px-2 py-1 border-carbs bg-carbs/30'>
                  <div>Carbs</div>
                  <div className='flex items-center gap-2'>
                    <input
                      type='number'
                      value={newIngredient.carbohydrates}
                      onChange={(e) =>
                        setNewIngredient({
                          ...newIngredient,
                          carbohydrates: e.target.value,
                        })
                      }
                      className='rounded-full px-2 py-1 bg-transparent text-white w-full text-center'
                    />
                    <span>g</span>
                  </div>
                </div>
                <div className='flex justify-between items-center border rounded-full px-2 py-1 border-fats bg-fats/30'>
                  <div>Fat</div>
                  <div className='flex items-center gap-2'>
                    <input
                      type='number'
                      value={newIngredient.fats}
                      onChange={(e) =>
                        setNewIngredient({
                          ...newIngredient,
                          fats: e.target.value,
                        })
                      }
                      className='rounded-full px-2 py-1 bg-transparent text-white w-full text-center'
                    />
                    <span>g</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className='flex items-center gap-2'>
            <button
              className={`flex-1 rounded-full py-2 bg-gradient-to-r from-[#fc00ff] to-[#00dbde] text-white text-center font-bold ${
                !newIngredient?.name && 'opacity-50'
              } ${isAnalyzing && 'animate-pulse'}`}
              disabled={!newIngredient?.name || isAnalyzing}
              onClick={analyzeDescription}
            >
              {isAnalyzing ? 'Analyzing...' : 'Analyze with AI'}
              {!user.activated && !user.programRegistered && (
                <span className='text-xs'> ({credit})</span>
              )}
            </button>
            <button
              className='flex-1 rounded-full py-2 bg-accent text-black font-bold'
              onClick={
                typeof isEditingIndex === 'number'
                  ? updateExistingIngredient
                  : addNewIngredientToList
              }
            >
              {typeof isEditingIndex === 'number' ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default EditIngredient;
